import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, AutocompleteAPI, Box, FormField } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import useDataCommon from "hooks/useDataCommon.hook";
import RoleService from "services/identity/role.service";
import UserProfileService from "services/medkit/userProfile.service";
import ProjectMemberService from "services/medkit/projectMember.service";

import { Tabs } from "components";
import { RootState } from "store";
import { DashboardLayout } from "layout";
import { BooleanInt, Status } from "constants/enum";
import { ICodename, IInput } from "commons/interfaces";
import { setDataAlert, showLoading } from "store/slice/common.slice";



const roleService = new RoleService();
const userProfileService = new UserProfileService();
const projectMemberService = new ProjectMemberService();

const ProjectMemberDetailScreen = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getDataUsers, getDataProjects } = useDataCommon();

    const user = useSelector((state: RootState) => state.user);
    const listPathName = useSelector((state: RootState) => state.titleRoute.listPathName);

    const [dataRoles, setDataRoles] = useState<ICodename[]>([]);
    const [itemUser, setItemUser] = useState<ICodename | undefined>(undefined);
    const [itemProject, setItemProject] = useState<ICodename | undefined>(undefined);

    const [tabIndex, setTabIndex] = useState(BooleanInt.False);
    const [dataInvite, setDataInvite] = useState<{
        userId?: IInput;
        projectId?: IInput;
    }>({});

    const [errorData, setErrorData] = useState<any>({});
    const [dataCreate, setDataCreate] = useState<{
        email?: string;
        fullName?: string;
        userRole?: string;
        projectId?: string;
        phoneNumber?: string;
        userTitle?: string;
        userName?: string;
        password?: string;
    }>({});

    const requestGetApiUser = useMemo(() => ({
        listStatus: [Status.Active],
        organizationId: user.workingOrganization || "0",
    }), [user.workingOrganization]);

    const arrButtonTab = [
        {
            value: BooleanInt.False,
            title: Strings.PROJECT_MEMBER_MANAGEMENT.MEMBER_ALREADY_ACCOUNT,
        },
        {
            value: BooleanInt.True,
            title: Strings.PROJECT_MEMBER_MANAGEMENT.MEMBER_NOT_ACCOUNT,
        },
    ];

    useEffect(() => {
        (async () => {
            try {
                const rows: ICodename[] = [];

                const result = await roleService.getAll({
                    status: [Status.Active],
                    organizationId: user?.workingOrganization,
                });

                for (const element of [...result || []]) {
                    rows.push({
                        code: element.roleCode,
                        name: element.roleName,
                        detail: element,
                    });
                };

                setDataRoles(rows);

            } catch (error) {

            }
        })();
    }, [user?.workingOrganization]);

    const handleChangeValue = (key: string, value: any) => {
        setErrorData((prev: any) => ({
            ...prev,
            [key]: undefined,
        }));
        setDataCreate((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleInviteMember = async () => {
        let checked = true;

        if (Helpers.isNullOrEmpty(dataInvite?.projectId?.value)) {
            checked = false;
            setDataInvite(prev => ({
                ...prev,
                projectId: {
                    error: Strings.PROJECT_MANAGEMENT.PLEASE_SELECT_PROJECT
                },
            }));
        };
        if (Helpers.isNullOrEmpty(dataInvite?.userId?.value)) {
            checked = false;
            setDataInvite(prev => ({
                ...prev,
                userId: {
                    error: Strings.PROJECT_MEMBER_MANAGEMENT.PLEASE_SELECT_MEMBER
                },
            }));
        };

        if (checked) {
            try {
                dispatch(showLoading(true));

                await projectMemberService.create({
                    userId: dataInvite?.userId?.value,
                    projectId: dataInvite?.projectId?.value,
                });

                dispatch(setDataAlert({ message: Strings.PROJECT_MEMBER_MANAGEMENT.CREATE_PROJECT_MEMBER_SUCCESS, type: "success" }));

                handleGoBack();

            } catch (error) {
                const err = Helpers.handleExceptionError(error);
                !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
            } finally {
                dispatch(showLoading(false));
            }
        } else {
            return;
        }
    };

    const handleCreateMember = async () => {
        let checked = true;
        let newError = { ...errorData };

        if (Helpers.isNullOrEmpty(dataCreate?.projectId)) {
            checked = false;
            newError["projectId"] = Strings.PROJECT_MANAGEMENT.PLEASE_SELECT_PROJECT;
        };
        if (Helpers.isNullOrEmpty(dataCreate?.fullName)) {
            checked = false;
            newError["fullName"] = Strings.PROJECT_MEMBER_MANAGEMENT.PLEASE_ENTER_FULL_NAME;
        };
        if (Helpers.isNullOrEmpty(dataCreate?.userName)) {
            checked = false;
            newError["userName"] = Strings.PROJECT_MEMBER_MANAGEMENT.PLEASE_ENTER_USER_NAME;
        };
        if (!Helpers.isNullOrEmpty(dataCreate?.email) && !Helpers.isValidEmail(dataCreate?.email)) {
            checked = false;
            newError["email"] = Strings.PROJECT_MEMBER_MANAGEMENT.WRONG_FORMAT_EMAIL;
        };
        if (!Helpers.isNullOrEmpty(dataCreate?.phoneNumber) && !Helpers.isValidPhoneNumber(dataCreate?.phoneNumber)) {
            checked = false;
            newError["phoneNumber"] = Strings.PROJECT_MEMBER_MANAGEMENT.WRONG_FORMAT_PHONE_NUMBER;
        };

        if (checked) {
            try {
                dispatch(showLoading(true));

                await userProfileService.invite({
                    email: dataCreate?.email,
                    fullname: dataCreate?.fullName,
                    phoneNumber: dataCreate?.phoneNumber,
                    roles: [dataCreate?.userRole],
                    projectId: dataCreate?.projectId,
                    organizationId: user?.workingOrganization,
                });

                dispatch(setDataAlert({ message: Strings.PROJECT_MEMBER_MANAGEMENT.CREATE_PROJECT_MEMBER_SUCCESS, type: "success" }));

                handleGoBack();
            } catch (error) {
                const err = Helpers.handleExceptionError(error);
                !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
            } finally {
                dispatch(showLoading(false));
            }
        } else {
            setErrorData(newError);
            return;
        }
    };

    const handleSubmit = () => {
        if (tabIndex === BooleanInt.False) {
            handleInviteMember();
        }
        if (tabIndex === BooleanInt.True) {
            handleCreateMember();
        }
    };

    const handleGoBack = () => {
        const itemP = listPathName.find((el) => el.pathName === Screens.PROJECT_MEMBER);
        navigate(Screens.PROJECT_MEMBER + (itemP ? itemP.query : ""));
    };

    return (
        <DashboardLayout
            isPermission
            allowSettingLayout
            requiredOrganization
            title={Strings.PROJECT_MEMBER_MANAGEMENT.TITLE_CREATE}
            route={[
                { title: Strings.PROJECT_MEMBER_MANAGEMENT.TITLE, route: Screens.PROJECT_MEMBER },
                { title: Strings.PROJECT_MEMBER_MANAGEMENT.TITLE_CREATE, route: "" },
            ]}
            listActions={[
                {
                    color: "secondary",
                    onPress: handleGoBack,
                    title: Strings.COMMON.BACK,
                },
                {
                    color: "dark",
                    onPress: handleSubmit,
                    title: Strings.COMMON.SAVE,
                },
            ]}
        >
            <Box sx={{
                pl: 2,
                pb: 1,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
            }}>
                <Tabs
                    value={tabIndex}
                    tabs={arrButtonTab}
                    onChangeTab={(item) => {
                        setTabIndex(item.value);
                        setErrorData({});
                        setDataCreate({});
                        setDataInvite({});
                    }}
                />
            </Box>
            <Card sx={{ padding: 3 }}>
                {(tabIndex === BooleanInt.False) &&
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <AutocompleteAPI
                                required
                                variant={"outlined"}
                                label={Strings.PROJECT_MANAGEMENT.PROJECT}
                                defaultValue={dataInvite?.projectId?.value}
                                errorMessage={dataInvite?.projectId?.error}
                                placeholder={Strings.PROJECT_MANAGEMENT.SELECT_PROJECT}
                                dataSelected={itemProject ? [itemProject] : []}
                                getApi={getDataProjects}
                                requestGetApi={{}}
                                onChange={(value) => { }}
                                onChangeItem={(value) => {
                                    setItemProject(value);
                                    setDataInvite(prev => ({
                                        ...prev,
                                        projectId: { value: value?.code },
                                    }));
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AutocompleteAPI
                                required
                                variant={"outlined"}
                                defaultValue={dataInvite?.userId?.value}
                                errorMessage={dataInvite?.userId?.error}
                                label={Strings.PROJECT_MEMBER_MANAGEMENT.MEMBER}
                                placeholder={Strings.PROJECT_MEMBER_MANAGEMENT.SELECT_MEMBER}
                                dataSelected={itemUser ? [itemUser] : []}
                                requestGetApi={requestGetApiUser}
                                getApi={(requestGetApi) => getDataUsers(requestGetApi, true)}
                                onChange={(value) => { }}
                                onChangeItem={(value) => {
                                    setItemUser(value);
                                    setDataInvite(prev => ({
                                        ...prev,
                                        userId: { value: value?.code },
                                    }));
                                }}
                            />
                        </Grid>
                    </Grid>
                }

                {(tabIndex === BooleanInt.True) &&
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <AutocompleteAPI
                                required
                                variant={"outlined"}
                                errorMessage={errorData?.projectId}
                                defaultValue={dataCreate?.projectId}
                                label={Strings.PROJECT_MANAGEMENT.PROJECT}
                                placeholder={Strings.PROJECT_MANAGEMENT.SELECT_PROJECT}
                                dataSelected={itemProject ? [itemProject] : []}
                                getApi={getDataProjects}
                                requestGetApi={{}}
                                onChange={(value) => { }}
                                onChangeItem={(value) => {
                                    setItemProject(value);
                                    handleChangeValue("projectId", value?.code);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField
                                required
                                variant={"outlined"}
                                value={dataCreate?.fullName}
                                errorMessage={errorData?.fullName}
                                label={Strings.PROJECT_MEMBER_MANAGEMENT.FULL_NAME}
                                placeholder={Strings.PROJECT_MEMBER_MANAGEMENT.ENTER_FULL_NAME}
                                onChangeValue={(val) => {
                                    handleChangeValue("fullName", val);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                data={dataRoles}
                                variant={"outlined"}
                                errorMessage={errorData?.userRole}
                                defaultValue={dataCreate?.userRole}
                                label={Strings.PROJECT_MEMBER_MANAGEMENT.ROLE}
                                placeholder={Strings.PROJECT_MEMBER_MANAGEMENT.SELECT_ROLE}
                                onChange={(value: any) => {
                                    handleChangeValue("userRole", value);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField
                                variant={"outlined"}
                                value={dataCreate?.email}
                                errorMessage={errorData?.email}
                                label={Strings.PROJECT_MEMBER_MANAGEMENT.EMAIL}
                                placeholder={Strings.PROJECT_MEMBER_MANAGEMENT.ENTER_EMAIL}
                                onChangeValue={(val) => {
                                    handleChangeValue("email", val);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField
                                variant={"outlined"}
                                value={dataCreate?.phoneNumber}
                                errorMessage={errorData?.phoneNumber}
                                label={Strings.PROJECT_MEMBER_MANAGEMENT.PHONE_NUMBER}
                                placeholder={Strings.PROJECT_MEMBER_MANAGEMENT.ENTER_PHONE_NUMBER}
                                onChangeValue={(val) => {
                                    handleChangeValue("phoneNumber", val);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField
                                variant={"outlined"}
                                value={dataCreate?.userTitle}
                                errorMessage={errorData?.userTitle}
                                label={Strings.PROJECT_MEMBER_MANAGEMENT.USER_TITLE}
                                placeholder={Strings.PROJECT_MEMBER_MANAGEMENT.ENTER_USER_TITLE}
                                onChangeValue={(val) => {
                                    handleChangeValue("userTitle", val);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField
                                required
                                variant={"outlined"}
                                value={dataCreate?.userName}
                                errorMessage={errorData?.userName}
                                label={Strings.PROJECT_MEMBER_MANAGEMENT.USER_NAME}
                                placeholder={Strings.PROJECT_MEMBER_MANAGEMENT.ENTER_USER_NAME}
                                onChangeValue={(val) => {
                                    handleChangeValue("userName", val);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField
                                secure
                                variant={"outlined"}
                                value={dataCreate?.password}
                                errorMessage={errorData?.password}
                                label={Strings.PROJECT_MEMBER_MANAGEMENT.PASSWORD}
                                placeholder={Strings.PROJECT_MEMBER_MANAGEMENT.ENTER_PASSWORD}
                                onChangeValue={(val) => {
                                    handleChangeValue("password", val);
                                }}
                            />
                        </Grid>
                    </Grid>
                }
            </Card>
        </DashboardLayout>
    );
};

export default ProjectMemberDetailScreen;
